import React from 'react';
import { Box } from '@mui/material';
import { NotifyPhaserEvents, PhaserToReactListeners, RemoveListener } from 'Phaser/EventBus2';
import {
  fingeringReportLabelOf,
  fingeringReportRecordOf,
  type SightReadingFingeringReport,
} from '../fingeringReport';
import { aidButtonSx, aidPanelSx } from './testingAidStyles';

const CONFIRMATION_MILLISECONDS = 3000;

/** A few words just above the control, so the toolbar it sits in keeps its height. */
const confirmationSx = {
  ...aidPanelSx,
  position: 'absolute',
  bottom: 'calc(100% + 6px)',
  left: 0,
  zIndex: 3000,
  whiteSpace: 'nowrap',
} as const;

/** The record to copy by hand, in the middle of the screen until it is dismissed. */
const shownRecordSx = {
  ...aidPanelSx,
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 3000,
  display: 'grid',
  gap: '6px',
  padding: '10px',
  boxShadow: '0 12px 36px rgba(0, 0, 0, 0.32)',
} as const;

const recordTextSx = {
  width: 'min(420px, calc(100vw - 48px))',
  height: 'min(160px, calc(100vh - 140px))',
  font: 'inherit',
  fontFamily: 'ui-monospace, Menlo, monospace',
  color: 'inherit',
  backgroundColor: 'rgba(255, 255, 255, 0.08)',
  border: '1px solid rgba(234, 242, 255, 0.3)',
  borderRadius: '6px',
  padding: '4px',
  resize: 'none',
} as const;

type Outcome =
  | { status: 'idle' }
  | { status: 'copied'; label: string }
  | { status: 'nothing to report' }
  /** The clipboard was refused: the record is shown, selected, to copy by hand. */
  | { status: 'shown'; text: string };

const IDLE: Outcome = { status: 'idle' };

/**
 * How the scene fingered the phrase under the cursor; null when no scene
 * answers or it has nothing to report. The bus hands every event over at once,
 * so the answer is in hand when the question returns: the copy that follows is
 * still part of the tap, which is what lets a phone browser allow it.
 */
const reportedByScene = (): SightReadingFingeringReport | null => {
  const answers: Array<SightReadingFingeringReport | null> = [];
  const keepAnswer = (reported: SightReadingFingeringReport | null) => {
    answers.push(reported);
  };
  PhaserToReactListeners.SightReadingFingeringReported(keepAnswer);
  NotifyPhaserEvents.ReportSightReadingFingering();
  RemoveListener('SightReadingFingeringReported', keepAnswer);
  return answers[0] ?? null;
};

/** Whether the text reached the clipboard. A browser refuses on a page that is
 *  not secure, without permission, or when it does not count the call as part
 *  of a tap. */
const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const recordTextOf = (reported: SightReadingFingeringReport): string =>
  JSON.stringify(
    fingeringReportRecordOf(reported, new Date(), {
      userAgent: window.navigator.userAgent,
      viewport: { width: window.innerWidth, height: window.innerHeight },
    }),
  );

const selectAll = (area: HTMLTextAreaElement | null) => {
  if (area === null) return;
  area.focus();
  // `select()` alone selects nothing on iOS.
  area.setSelectionRange(0, area.value.length);
};

/**
 * Testing aid for live finger numbers: one tap copies a record of the phrase
 * the cursor is on, as it was fingered and drawn, to paste into a report.
 * Where the browser refuses the clipboard the record is shown selected instead.
 */
const FingeringReportControl = () => {
  const [outcome, setOutcome] = React.useState<Outcome>(IDLE);

  React.useEffect(() => {
    if (outcome.status !== 'copied' && outcome.status !== 'nothing to report') return undefined;
    const fading = window.setTimeout(() => setOutcome(IDLE), CONFIRMATION_MILLISECONDS);
    return () => window.clearTimeout(fading);
  }, [outcome]);

  const reportFingering = async () => {
    const reported = reportedByScene();
    if (reported === null) {
      setOutcome({ status: 'nothing to report' });
      return;
    }
    const text = recordTextOf(reported);
    const copied = await copyToClipboard(text);
    setOutcome(
      copied
        ? { status: 'copied', label: fingeringReportLabelOf(reported) }
        : { status: 'shown', text },
    );
  };

  return (
    <Box data-fingering-report-control sx={{ position: 'relative', display: 'inline-flex' }}>
      <Box component="button" type="button" onClick={() => void reportFingering()} sx={aidButtonSx}>
        Report this fingering
      </Box>
      {outcome.status === 'copied' && (
        <Box role="status" data-fingering-report-outcome="copied" sx={confirmationSx}>
          Copied: {outcome.label}
        </Box>
      )}
      {outcome.status === 'nothing to report' && (
        <Box role="status" data-fingering-report-outcome="nothing" sx={confirmationSx}>
          No fingered phrase on screen yet
        </Box>
      )}
      {outcome.status === 'shown' && (
        <Box data-fingering-report-outcome="shown" sx={shownRecordSx}>
          <Box role="status">Copying was not allowed. Copy the selected text:</Box>
          <Box
            component="textarea"
            readOnly
            value={outcome.text}
            ref={selectAll}
            onFocus={(event: React.FocusEvent<HTMLTextAreaElement>) => selectAll(event.target)}
            aria-label="Fingering record"
            sx={recordTextSx}
          />
          <Box
            component="button"
            type="button"
            onClick={() => setOutcome(IDLE)}
            sx={{ ...aidButtonSx, justifySelf: 'end' }}
          >
            Done
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FingeringReportControl;
